/**
 * Minimal agent startup — registers only the services needed for
 * AI inspection tools (hierarchy, properties, AI tools).
 *
 * Does NOT import editor.common.ts (UI commands) or create any renderers.
 * Visual inspector features (InspectorView, ThreeEditor, KTX2/DRACO loaders)
 * are excluded from this entry point.
 */

import { Application, IApplication } from "../services/application/common/application.js";
import { toLocalISOString } from "../../base/common/date.js";
import { URI } from "../../base/common/uri.js";
import { IAIContextService, IAIService, IAIToolService } from "../../platform/ai/common/ai.js";
import { AIContextService } from "../../platform/ai/common/aiContext.js";
import { AIService } from "../../platform/ai/common/aiService.js";
import { AIHierarchyContext } from "../../platform/ai/common/context/hierarchyContext.js";
import { AIEditorStateContext } from "../../platform/ai/common/context/editorStateContext.js";
import { AgentAIToolService } from "./agentToolService.js";
import { FindPropertiesTool, ReadPropertyTool } from "../../platform/ai/common/tools/propertyTools.js";
import { ListResourcesTool } from "../../platform/ai/common/tools/resourceTools.js";
import { ReadCodeTool } from "../../platform/ai/common/tools/codeTools.js";
import { MeasurePerformanceTool } from "../../platform/ai/browser/performanceTool.js";
import { ComponentSearchTool } from "../services/needle/componentSearchTool.js";
import { LocalStorageFileSystemProvider } from "../../platform/files/browser/localStorageFileSystemProvider.js";
import { IFileService } from "../../platform/files/common/files.js";
import { FileService } from "../../platform/files/common/fileService.js";
import { SyncDescriptor } from "../../platform/instantiation/common/descriptors.js";
import { IInstantiationService } from "../../platform/instantiation/common/instantiation.js";
import { InstantiationService } from "../../platform/instantiation/common/instantiationService.js";
import { getSingletonServiceDescriptors } from "../../platform/instantiation/common/registry.js";
import { ServiceCollection } from "../../platform/instantiation/common/serviceCollection.js";
import { BufferLogger } from "../../platform/log/common/bufferLog.js";
import { FileLoggerService } from "../../platform/log/common/fileLog.js";
import { ConsoleLogger, ILogger, ILoggerService, ILogService, LogLevel } from "../../platform/log/common/log.js";
import { LogService } from "../../platform/log/common/logService.js";
import { IWebGLService } from "../../platform/webgl/common/webgl.js";
import { IGpuTimingService } from "../../platform/webgl/common/gpuTiming.js";
import { GpuTimingService } from "../../platform/webgl/browser/gpuTimingService.js";
import { WebGLService } from "../../platform/webgl/common/webglService.js";
import { ThreeResourceTracker } from "../../platform/webgl/three.js/resourceTracker.js";
import { ThreeTreeProvider } from "../../platform/webgl/three.js/treeProvider.js";
import { ThreePropertyProvider } from "../../platform/webgl/three.js/propertyProvider.js";
import { IPropertyAccessorService } from "../../platform/properties/common/accessor.js";
import { IProjectService } from "../services/project/common/project.js";
import { BasicProjectService } from "../services/project/browser/projectService.js";
import { IPropertyService } from "../../platform/properties/common/properties.js";
import { PropertyService } from "../../platform/properties/common/propertyService.js";
import { PropertyAccessorService } from "../../platform/properties/common/accessorService.js";
import { IHierarchyService, ITreeProviderService } from "../../platform/hierarchy/common/hierarchy.js";
import { HierarchyService } from "../../platform/hierarchy/common/hierarchyService.js";
import { TreeProviderService } from "../../platform/hierarchy/common/treeProvider.js";
import { IStorageService } from "../../platform/storage/common/storage.js";
import { BrowserStorageService } from "../services/storage/browser/storageService.js";
import { ConfigurationService } from "../../platform/configuration/common/configurationService.js";
import { IConfigurationService } from "../../platform/configuration/common/configuration.js";
import { ContextKeyService } from "../../platform/contextkey/browser/contextKeyService.js";
import { IContextKeyService } from "../../platform/contextkey/common/contextkey.js";
import { IProductService, ProductService } from "../../platform/product/common/productService.js";
import { product } from "../../platform/product/common/product.js";
import { IUserConfiguration } from "../services/user-configuration/common/userConfiguration.js";
import { UserConfiguration } from "../services/user-configuration/common/userConfiguration.impl.js";
import { onUnexpectedError } from "../../base/common/errors.js";
import { IUndoRedoService } from "../../platform/undo/common/undo.js";
import { UndoRedoService } from "../../platform/undo/common/undoService.js";
import { INotificationService } from "../../platform/notifications/common/notifications.js";
import { NotificationService } from "../../platform/notifications/common/notificationService.js";
import { IMenuService } from "../../platform/actions/common/actions.js";
import { MenuService } from "../../platform/actions/common/menuService.js";
import { IContextMenuService } from "../../platform/contextview/common/context.js";
import { ContextMenuService } from "../../platform/contextview/common/contextMenuService.js";
import { IAnalyticsService } from "../../platform/analytics/common/analytics.js";
import { ITracingService } from "../../platform/tracing/common/tracing.js";
import { TracingService } from "../../platform/tracing/common/tracingService.js";
import { RybbitAnalyticsService } from "../../platform/analytics/browser/RybbitAnalyticsService.js";
import { IIdentitiyService } from "../../platform/auth/common/auth.js";
import { ILicenseService } from "../../platform/auth/common/licenses.js";
import { AgentIdentityService } from "../../platform/auth/agent/identityService.js";
import { PreviewLicenseService } from "../../platform/auth/agent/licenseService.js";
import { IWindowsMainService } from "../../platform/windows/common/windows.js";
import { WindowsMainService } from "../../platform/windows/browser/windowsMainService.js";
import { IKeyboardService } from "../../platform/keyboard/common/keyboardService.js";
import { BrowserKeyboardService } from "../../platform/keyboard/browser/keyboardService.js";
import { IPointerService } from "../../platform/pointer/common/pointer.js";
import { BrowserPointerService } from "../../platform/pointer/browser/pointerService.js";
import { INavigationDeviceService } from "../../platform/navigation/common/navigationDevice.js";
import { BrowserNavigationDeviceService } from "../../platform/navigation/browser/navigationDeviceService.js";
import { IResourceService } from "../../platform/resources/common/resources.js";
import { createAgentEngineShim } from "./engineShim.js";
import { AIAgentPreviewContext } from "./previewContext.js";
import { ResourceService } from "../../platform/resources/common/resourcesService.js";
import { IResourceAnalysisService, ResourceAnalysisService } from "../../platform/webgl/three.js/resourceAnalysisService.js";
import { IInspectionService } from "../../platform/inspection/common/inspection.js";
import { InspectionService } from "../../platform/inspection/common/inspectionService.js";
import { IPreviewService } from "../../platform/preview/common/preview.js";
import { PreviewService } from "../../platform/preview/common/previewService.js";
import { IAnyWorkspaceIdentifier } from "../../platform/workspace/common/workspace.js";
import { IPerformanceService } from "../services/performance/common/performance.js";
import { BrowserPerformanceService } from "../services/performance/browser/performanceService.js";
import "../../platform/hierarchy/common/treeCommands.js";
import { INodeGraphInspectionService } from "../../platform/webgl/three.js/nodeGraphInspectionService.js";


export function startup(): Promise<IApplication> {
    return new Promise(async resolve => {
        const [instantiationService, _services, userConfiguration] = await registerServices();

        instantiationService.invokeFunction(async accessor => {
            const application = instantiationService.createInstance<IApplication>(new SyncDescriptor(Application, [false]));

            // Register hierarchy context for AI tools
            const context = accessor.get(IAIContextService);
            if (context) {
                const appContext = instantiationService.createInstance<AIHierarchyContext>(new SyncDescriptor(AIHierarchyContext));
                context.registerProvider(appContext);
                const editorState = instantiationService.createInstance<AIEditorStateContext>(new SyncDescriptor(AIEditorStateContext));
                context.registerProvider(editorState);
                // headless: the model is the only thing in the room, so the preview
                // limits have to be stated in what it receives
                context.registerProvider(new AIAgentPreviewContext());
            }

            // Register the Three.js tree provider so the hierarchy service can
            // walk Three.js scene graphs. We do NOT register the full ThreeJs engine
            // (which hooks scene.onBeforeRender and conflicts with the host engine).
            const treeProviderService = accessor.get(ITreeProviderService);
            if (treeProviderService) {
                treeProviderService.registerProvider(
                    instantiationService.createInstance<ThreeTreeProvider>(new SyncDescriptor(ThreeTreeProvider))
                );
            }

            // Populate the resource registry (materials/textures/geometries) from the
            // hierarchy — weak references only; safe next to a host-owned engine.
            instantiationService.createInstance<ThreeResourceTracker>(new SyncDescriptor(ThreeResourceTracker));

            const hierarchyService = accessor.get(IHierarchyService);
            const engineShim = createAgentEngineShim(hierarchyService, accessor.getIfExists(IResourceService));
            accessor.get(IWebGLService).registerEngine(engineShim);

            // Register ThreePropertyProvider so property_find / property_read
            // tools can inspect Object3D properties. The full ThreeJs engine is
            // NOT registered (it owns editor rendering and conflicts with the host).
            const propertyService = accessor.get(IPropertyService) as PropertyService;
            if (propertyService) {
                try {
                    // resolves the SAME addresses the browser editor mints (uuid
                    // first, then the resource registry) — a node-id-only lookup
                    // left every address unresolved headless
                    propertyService.registerProvider(
                        instantiationService.createInstance<ThreePropertyProvider>(
                            new SyncDescriptor(ThreePropertyProvider, [engineShim])
                        )
                    );
                } catch (err) {
                    console.error("[Needle Inspector Agent] Failed to register ThreePropertyProvider:", err);
                }
            }

            await Promise.allSettled([
                userConfiguration.initialize()
            ]);

            resolve(application);
        });
    });
}


async function registerServices(): Promise<[InstantiationService, ServiceCollection, IUserConfiguration]> {
    const services = new ServiceCollection(...getSingletonServiceDescriptors());

    // The headless product intentionally has no document-target service. The
    // singleton node-graph inspector requires one and is optional on Application,
    // so override its global descriptor instead of constructing a broken service.
    services.set(INodeGraphInspectionService, null as any);

    const productService = new ProductService({
        ...product,
        nameLong: "Needle Inspector Agent",
        nameShort: "Inspector Agent",
    });
    services.set(IProductService, productService);

    const instantiationService = new InstantiationService(services, false, undefined, false);
    services.set(IInstantiationService, instantiationService);

    // Logging
    const logLevel = LogLevel.Warning;
    const logsPath = URI.file(toLocalISOString(new Date()).replace(/-|:|\.\d+Z$/g, '')).with({ scheme: 'needle-log' });
    const fileLogger = new BufferLogger(logLevel);
    const fileService = new FileService(fileLogger);
    services.set(IFileService, fileService);
    const loggerService = new FileLoggerService(logLevel, logsPath, fileService);
    services.set(ILoggerService, loggerService);
    const otherLoggers: ILogger[] = [new ConsoleLogger(logLevel)];
    const logService = new LogService(fileLogger, otherLoggers);
    services.set(ILogService, logService);

    // Storage
    const storage = instantiationService.createInstance<LocalStorageFileSystemProvider>(new SyncDescriptor(LocalStorageFileSystemProvider));
    fileService.registerProvider('file', storage);

    const workspace: IAnyWorkspaceIdentifier = { id: window.location.host || 'agent' };
    const storageService = new BrowserStorageService(workspace, logService);
    try { await storageService.initialize(); }
    catch (error) { onUnexpectedError(error); }
    services.set(IStorageService, storageService);

    // Config
    services.set(IConfigurationService, new ConfigurationService());
    services.set(IContextKeyService, new SyncDescriptor(ContextKeyService));

    // Required by Application constructor
    services.set(IUndoRedoService, new SyncDescriptor(UndoRedoService));
    services.set(IKeyboardService, new SyncDescriptor(BrowserKeyboardService, [{}]));
    services.set(IPointerService, new SyncDescriptor(BrowserPointerService));
    services.set(INavigationDeviceService, new SyncDescriptor(BrowserNavigationDeviceService));
    services.set(INotificationService, new SyncDescriptor(NotificationService));
    services.set(IContextMenuService, new SyncDescriptor(ContextMenuService));
    services.set(IMenuService, new SyncDescriptor(MenuService));
    services.set(IWindowsMainService, new SyncDescriptor(WindowsMainService, [{ allowPopout: false }]));
    services.set(IResourceService, new SyncDescriptor(ResourceService));
    services.set(IResourceAnalysisService, new SyncDescriptor(ResourceAnalysisService));
    services.set(IInspectionService, new SyncDescriptor(InspectionService));
    services.set(IPreviewService, new SyncDescriptor(PreviewService));

    // Analytics
    const rybbit = instantiationService.createInstance<RybbitAnalyticsService>(new SyncDescriptor(RybbitAnalyticsService, [{
        siteId: "a4c4c70027a1",
        trackErrors: true,
    }]));
    services.set(IAnalyticsService, rybbit);
    services.set(ITracingService, new SyncDescriptor(TracingService));

    // No-op identity (agent has no login)
    services.set(IIdentitiyService, new AgentIdentityService());
    services.set(ILicenseService, new PreviewLicenseService());

    // User config
    const config = instantiationService.createInstance<IUserConfiguration>(new SyncDescriptor(UserConfiguration));
    services.set(IUserConfiguration, config);

    // Properties
    services.set(IPropertyAccessorService, new SyncDescriptor(PropertyAccessorService));

    // WebGL
    services.set(IWebGLService, new SyncDescriptor(WebGLService));
    services.set(IPerformanceService, new SyncDescriptor(BrowserPerformanceService, [{
        isChromeExtension: false,
    }]));
    // the render-graph service asks for it; without it the AI's graph tool would
    // quietly resolve to null here
    services.set(IGpuTimingService, new SyncDescriptor(GpuTimingService));

    // Hierarchy
    services.set(IHierarchyService, new SyncDescriptor(HierarchyService));
    services.set(ITreeProviderService, new SyncDescriptor(TreeProviderService));
    services.set(IPropertyService, new SyncDescriptor(PropertyService));
    services.set(IProjectService, new SyncDescriptor(BasicProjectService));

    // AI tools
    const aiContext = new AIContextService();
    services.set(IAIContextService, aiContext);

    // AgentAIToolService, not the plain one: headless has no ChatAI, so this is
    // the only place tool use is reported (see agentToolService.ts)
    const toolService = instantiationService.createInstance<IAIToolService>(new SyncDescriptor(AgentAIToolService));
    services.set(IAIToolService, toolService);

    const ai = instantiationService.createInstance<IAIService>(new SyncDescriptor(AIService));
    services.set(IAIService, ai);

    toolService.registerTool(instantiationService.createInstance<FindPropertiesTool>(new SyncDescriptor(FindPropertiesTool)));
    toolService.registerTool(instantiationService.createInstance<ReadPropertyTool>(new SyncDescriptor(ReadPropertyTool)));
    toolService.registerTool(instantiationService.createInstance<MeasurePerformanceTool>(new SyncDescriptor(MeasurePerformanceTool)));
    toolService.registerTool(instantiationService.createInstance<ComponentSearchTool>(new SyncDescriptor(ComponentSearchTool)));
    toolService.registerTool(instantiationService.createInstance<ListResourcesTool>(new SyncDescriptor(ListResourcesTool)));
    toolService.registerTool(instantiationService.createInstance<ReadCodeTool>(new SyncDescriptor(ReadCodeTool)));

    return [instantiationService, services, config];
}
